(() => {
  const root = () => window.Shopify?.routes?.root || '/';
  const parseSection = (html, selector) => {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  };

  const announce = (message) => {
    document.querySelectorAll('[data-cart-status]').forEach((node) => {
      node.textContent = message;
    });
  };

  const replaceSection = (html, selector) => {
    const next = parseSection(html, selector);
    const current = document.querySelector(selector);
    if (next && current) current.replaceWith(next);
  };

  const renderSections = (sections = {}) => {
    const drawerWasOpen = document.querySelector('[data-cart-drawer-root]')?.classList.contains('is-open');
    replaceSection(sections.header, '[data-anipura-header]');
    replaceSection(sections['cart-drawer'], '[data-cart-drawer-root]');
    replaceSection(sections.cart, '[data-cart-page-root]');
    bind();
    if (drawerWasOpen) {
      const drawer = document.querySelector('[data-cart-drawer-root]');
      if (drawer) {
        drawer.hidden = false;
        drawer.classList.add('is-open');
        document.body.classList.add('anipura-cart-open');
      }
    }
  };

  const fetchSections = async () => {
    const response = await fetch(`${root()}?sections=header,cart-drawer,cart`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart could not be refreshed.');
    renderSections(await response.json());
  };

  const request = async (path, options) => {
    const response = await fetch(`${root()}${path}`, options);
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Cart could not be updated.');
    if (data.sections) renderSections(data.sections);
    else await fetchSections();
    return data;
  };

  const open = () => {
    const drawer = document.querySelector('[data-cart-drawer-root]');
    if (!drawer) return;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.classList.add('anipura-cart-open');
    drawer.querySelector('[data-cart-close]')?.focus();
  };

  const close = () => {
    const drawer = document.querySelector('[data-cart-drawer-root]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('anipura-cart-open');
    window.setTimeout(() => {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 220);
  };

  const setBusy = (busy) => {
    document.querySelectorAll('[data-cart-quantity-button],[data-cart-remove]').forEach((control) => {
      control.disabled = busy;
      control.setAttribute('aria-busy', String(busy));
    });
  };

  const pending = new Map();
  const scheduleChange = (line, quantity) => {
    const existing = pending.get(line);
    if (existing) window.clearTimeout(existing.timer);
    document.querySelectorAll(`[data-cart-line="${line}"] [data-cart-quantity-output]`).forEach((node) => {
      node.textContent = quantity;
    });
    const timer = window.setTimeout(async () => {
      setBusy(true);
      announce('Updating cart…');
      try {
        await request('cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line,
            quantity,
            sections: ['header', 'cart-drawer', 'cart'],
            sections_url: window.location.pathname,
          }),
        });
        announce('Cart updated.');
      } catch (error) {
        await fetchSections().catch(() => {});
        announce(error.message);
      } finally {
        pending.delete(line);
        setBusy(false);
      }
    }, 180);
    pending.set(line, { timer, quantity });
  };

  const bind = () => {
    document.querySelectorAll('[data-cart-open]:not([data-cart-bound])').forEach((control) => {
      control.dataset.cartBound = 'true';
      control.addEventListener('click', (event) => {
        event.preventDefault();
        open();
      });
    });
    document.querySelectorAll('[data-cart-close]:not([data-cart-bound])').forEach((control) => {
      control.dataset.cartBound = 'true';
      control.addEventListener('click', close);
    });
    document.querySelectorAll('[data-cart-quantity-button]:not([data-cart-bound])').forEach((control) => {
      control.dataset.cartBound = 'true';
      control.addEventListener('click', () => {
        const item = control.closest('[data-cart-line]');
        const line = Number(item?.dataset.cartLine || 0);
        const output = item?.querySelector('[data-cart-quantity-output]');
        const current = Number(output?.textContent || 1);
        const delta = Number(control.dataset.cartQuantityButton || 0);
        scheduleChange(line, Math.max(0, current + delta));
      });
    });
    document.querySelectorAll('[data-cart-remove]:not([data-cart-bound])').forEach((control) => {
      control.dataset.cartBound = 'true';
      control.addEventListener('click', () => scheduleChange(Number(control.closest('[data-cart-line]')?.dataset.cartLine || 0), 0));
    });
    document.querySelectorAll('[data-shipping-calculator]:not([data-cart-bound])').forEach((form) => {
      form.dataset.cartBound = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const result = form.querySelector('[data-shipping-results]');
        const button = form.querySelector('button[type="submit"]');
        const province = form.elements.province.value.trim();
        const zip = form.elements.zip.value.trim();
        result.textContent = 'Calculating available rates…';
        button.disabled = true;
        try {
          const query = new URLSearchParams({
            'shipping_address[country]': 'Thailand',
            'shipping_address[province]': province,
            'shipping_address[zip]': zip,
          });
          const response = await fetch(`${root()}cart/shipping_rates.json?${query}`, { headers: { Accept: 'application/json' } });
          const data = await response.json();
          if (!response.ok) throw new Error(data.error || 'Delivery rates are not available for this location.');
          if (!data.shipping_rates?.length) throw new Error('No delivery rate is available for this location.');
          result.replaceChildren(...data.shipping_rates.map((rate) => {
            const row = document.createElement('p');
            const name = document.createElement('span');
            const price = document.createElement('strong');
            name.textContent = rate.name;
            price.textContent = `${rate.price} ${rate.currency || 'THB'}`;
            row.append(name, price);
            return row;
          }));
        } catch (error) {
          result.textContent = error.message;
        } finally {
          button.disabled = false;
        }
      });
    });
  };

  window.AnipuraCart = {
    open,
    close,
    refresh: fetchSections,
    add: async (form) => {
      const body = new FormData(form);
      body.append('sections', 'header,cart-drawer,cart');
      body.append('sections_url', window.location.pathname);
      const data = await request('cart/add.js', { method: 'POST', body, headers: { Accept: 'application/json' } });
      open();
      return data;
    },
  };

  document.addEventListener('click', (event) => {
    if (event.target.matches('[data-cart-drawer-root]')) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
  document.addEventListener('DOMContentLoaded', bind);
  document.addEventListener('shopify:section:load', bind);
})();
